import { Button, Input, Space, Table, Typography, Grid, App as AntApp, Alert } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";
import { useAuth } from "../app/auth.tsx";
import { CreateFamilyDrawer } from "../components/CreateFamilyDrawer.tsx";

type FamilyListItem = components["schemas"]["FamilyListItem"];

export function FamiliesPage() {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [creating, setCreating] = useState(false);

  const query = useQuery({
    queryKey: ["families", { search, page, pageSize }],
    queryFn: async () => {
      const { data, error } = await api.GET("/families", { params: { query: { ...(search ? { Search: search } : {}), Page: page, PageSize: pageSize } } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
    placeholderData: keepPreviousData,
  });

  const columns = [
    {
      title: t("families.columns.name"),
      dataIndex: "name",
      key: "name",
      ellipsis: true,
      render: (name: string, row: FamilyListItem) => (
        <Link to={`/families/${row.id}`} aria-label={t("families.open")} onClick={(e) => e.stopPropagation()}>
          {name}
        </Link>
      ),
    },
    { title: t("families.columns.payer"), dataIndex: "payerName", key: "payerName", ellipsis: true, responsive: ["md" as const] },
    { title: t("families.columns.phone"), dataIndex: "payerPhone", key: "payerPhone", width: 160 },
    { title: t("families.columns.students"), dataIndex: "studentCount", key: "studentCount", width: 110, align: "right" as const },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between" }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t("families.title")}
        </Typography.Title>
        <Space wrap>
          <Input.Search
            allowClear
            placeholder={t("families.search")}
            aria-label={t("families.search")}
            style={{ width: screens.md ? 320 : 220 }}
            onSearch={(value) => {
              setSearch(value.trim());
              setPage(1);
            }}
          />
          {hasPermission("people.manage") && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
              {t("families.create")}
            </Button>
          )}
        </Space>
      </div>
      {query.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void query.refetch()}>{t("app.retry")}</Button>} />}
      <Table<FamilyListItem>
        rowKey="id"
        size={screens.md ? "middle" : "small"}
        loading={query.isPending}
        columns={columns}
        dataSource={query.data?.items ?? []}
        locale={{ emptyText: t("families.empty") }}
        scroll={{ x: 480 }}
        onRow={(row) => ({ onClick: () => navigate(`/families/${row.id}`), style: { cursor: "pointer" } })}
        pagination={{
          current: page,
          pageSize,
          total: Number(query.data?.totalCount ?? 0),
          showSizeChanger: true,
          showTotal: (total) => t("families.total", { count: total }),
          onChange: (nextPage, nextSize) => {
            setPage(nextPage);
            setPageSize(nextSize);
          },
        }}
      />
      <CreateFamilyDrawer
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(family) => {
          setCreating(false);
          void message.success(t("families.created", { name: family.name }));
          navigate(`/families/${family.id}`);
        }}
      />
    </Space>
  );
}
